import { Link, useNavigate, useSearchParams } from 'react-router-dom' 
import { 
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb' 
import AgentLayout from './components/AgentLayout'
import { useMarketResearchByIdQuery } from '@/queries/useMarketResearchByIdQuery'
import { useEffect, useState } from 'react'
import { MarketResearch } from '@/types/MarketResearch'
import { Camera, CircleArrowLeft, Package, RefreshCw, Search, Users } from 'lucide-react'
import { formatDateToString } from '@/utils/date'
import { TypographyXS, TypographyH1, TypographyMuted } from '@/components/ui/typography'
import { useResearchedProductQuery } from '@/queries/useResearchedProductQuery'
import ButtonWithTooltip from '@/components/ButtonWithTooltip'
import { useBaseStore } from '@/store'
import { Store } from '@/store/type'
import AdmLayout from './components/AdmLayout'
import { useResearchCoverageQuery } from '@/queries/useResearchCoverageQuery'
import { numberFormat } from '@/utils/stringFormatter'
import Button from '@/components/Button'
import LoadingScreen from '@/pages/LoadingScreen'
import ResearchCoverageDialog from './components/ResearchCoverageDialog'
import ProductsNotSearchedDialog from './components/ProductsNotSearchedDialog'
import useDisclosure from '@/hooks/useDisclosure'
 
const stateSelector = (state: Store) => ({ 
    profile: state.authSlice.state.profile
})

interface TotalCoverage {
    CLIENTES_PESQUISA: number
    CLIENTES_PESQUISADOS: number
    TOTAL_PRODUTOS_PESQUISA: number
    TOTAL_PRODUTOS_PESQUISADOS: number
}

const MarketResearchDetails = () => { 
    const navigate = useNavigate() 
    const [searchParams] = useSearchParams(); 
    const id = searchParams.get("search");  
    const [dataMarketResearch, setDataMarketResearch] = useState<MarketResearch>() 
    const { profile } = useBaseStore(stateSelector)
    const [totalCoverage, setTotalCoverage] = useState<TotalCoverage>({
        CLIENTES_PESQUISA: 0,
        CLIENTES_PESQUISADOS: 0,
        TOTAL_PRODUTOS_PESQUISA: 0,
        TOTAL_PRODUTOS_PESQUISADOS: 0,
    });

    if(!id) {
        navigate('/market-research')
    }

    const {
        isOpen: isOpenResearchCoverageDialog,
        onClose: onCloseResearchCoverageDialog,
        onOpen: onOpenResearchCoverageDialog
    } = useDisclosure()

    const {
        isOpen: isOpenProductsNotSearchedDialog,
        onClose: onCloseProductsNotSearchedDialog,
        onOpen: onOpenProductsNotSearchedDialog
    } = useDisclosure()

    const { data ,isPending } = useMarketResearchByIdQuery(Number(id)) 
    const { data: dataResearchedProduct, refetch: refetchResearchedProduct, isFetching } = useResearchedProductQuery(Number(id)) 
    const { data: dataResearchCoverage, refetch: refetchResearchCoverage } = useResearchCoverageQuery(Number(id)) 
    
    useEffect(() => {
        data && setDataMarketResearch(data[0])
        console.log('data', data)
    }, [data]); 
 
    useEffect(() => {
        if(dataResearchCoverage) {
            const resultResearch = dataResearchCoverage.reduce<TotalCoverage>(
                (acc, item) => {
                    acc.CLIENTES_PESQUISA += item.CLIENTES_PESQUISA ?? 0;
                    acc.CLIENTES_PESQUISADOS += item.CLIENTES_PESQUISADOS ?? 0;
                    acc.TOTAL_PRODUTOS_PESQUISA += item.TOTAL_PRODUTOS_PESQUISA ?? 0;
                    acc.TOTAL_PRODUTOS_PESQUISADOS += item.TOTAL_PRODUTOS_PESQUISADOS ?? 0;
                    return acc;
                },
                {
                    CLIENTES_PESQUISA: 0,
                    CLIENTES_PESQUISADOS: 0,
                    TOTAL_PRODUTOS_PESQUISA: 0,
                    TOTAL_PRODUTOS_PESQUISADOS: 0,
                }
            );
            setTotalCoverage(resultResearch)
        }
    },[dataResearchCoverage])

    if (isPending) return <LoadingScreen />

    return (
        <div className="flex flex-col h-full overflow-auto p-4 bg-gray-50">
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link
                                className="hover:underline"
                                to="/market-research"
                            >
                                Planejamento de Pesquisa
                            </Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator /> 
                    <BreadcrumbItem>
                        <BreadcrumbLink> 
                                Detalhes 
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator /> 
                    <BreadcrumbItem>
                        <BreadcrumbPage>
                            #{id} {dataMarketResearch && dataMarketResearch.NM_PESQUISA}  
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            {dataMarketResearch && (
                <div className='flex justify-between my-4'>
                    <div className="flex flex-col gap-4 justify-between sm:items-center sm:flex-row">
                        <div className="flex gap-3 items-center">
                            <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
                                <Search className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <TypographyH1 className="text-2xl font-semibold">
                                     {dataMarketResearch.NM_PESQUISA } 
                                </TypographyH1>
                                <TypographyMuted>
                                    {dataMarketResearch.DS_REVENDA}
                                </TypographyMuted>
                                <TypographyMuted>
                                   {formatDateToString(dataMarketResearch.SK_TEMPO_INICIO)} - {" "} {formatDateToString(dataMarketResearch.SK_TEMPO_FIM)}
                                </TypographyMuted>
                            </div>
                        </div>
                    </div>  
                    <div className="flex gap-6">    
                        <Button className="md:flex hidden flex-col items-center text-xs" onClick={() => navigate(-1)}>
                            <CircleArrowLeft size={18} />
                            <TypographyMuted className="text-white">Voltar</TypographyMuted> 
                        </Button>
                        <ButtonWithTooltip
                            tooltipContent={<p className="text-white">Atualizar</p>}
                            variant="ghost"
                            isLoading={isFetching}
                            onClick={() => {
                                refetchResearchedProduct()
                                refetchResearchCoverage()
                            }}
                            className="shadow  bg-white"
                        >
                            <RefreshCw size={18} /> 
                        </ButtonWithTooltip>
                        
                    </div>  
                </div>
            )}
            {totalCoverage && (
                <div className='bg-white rounded-2xl p-5 shadow-sm border border-gray-200'>
                      {dataMarketResearch?.DD_COLETA_IMAGEM 
                            ?  (
                                <TypographyXS className="text-sm font-medium text-gray-900 flex gap-2 mb-4">
                                    <Camera size={18}/> 
                                    Obrigatorio o envio da imagem
                                </TypographyXS>
                            )
                            : '' 
                        }
                    <div className="md:flex grid  gap-4">
                        <div className="info-item flex-1">
                            <Users size={14}/>
                            <div>
                                <p className="text-xs text-gray-600">Clientes</p>
                                <p className="text-sm font-bold text-gray-900"> {numberFormat(totalCoverage.CLIENTES_PESQUISA,0)} / {numberFormat(totalCoverage.CLIENTES_PESQUISADOS,0)} </p>
                            </div>
                        </div>
                        <div className="info-item flex-1">
                            <Package size={14}/>
                            <div>
                                <p className="text-xs text-gray-600">Total produtos</p>
                                <p className="text-sm font-bold text-gray-900"> {numberFormat(totalCoverage.TOTAL_PRODUTOS_PESQUISA,0)} / {numberFormat(totalCoverage.TOTAL_PRODUTOS_PESQUISADOS,0)} </p>
                            </div>
                        </div>
                        <div className="info-item flex gap-2"> 
                            <Button onClick={() => onOpenResearchCoverageDialog()}>
                                Cobertura por funcionário
                            </Button>
                            <Button variant="outline" onClick={() => onOpenProductsNotSearchedDialog()}>
                                Produtos não pesquisados
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {dataResearchCoverage && (
                <ResearchCoverageDialog 
                    isOpen={isOpenResearchCoverageDialog}
                    onClose={onCloseResearchCoverageDialog}
                    researchCoverage={dataResearchCoverage}
                />
            )}

            {dataMarketResearch && (
                <ProductsNotSearchedDialog 
                    isOpen={isOpenProductsNotSearchedDialog}
                    onClose={onCloseProductsNotSearchedDialog}
                    skPesquisa={dataMarketResearch.SK_PESQUISA}
                />
            )}

            {dataMarketResearch?.SK_PESQUISA &&  dataResearchedProduct && (
                <> 
                    {profile == 'REPRESENTANTE' && (
                        <div>
                            <AgentLayout marketResearch={dataMarketResearch} dataResearchedProducts={dataResearchedProduct} />
                        </div>
                    )}

                    {profile == 'ADMINISTRADOR' && (
                        <div>
                            <AdmLayout marketResearch={dataMarketResearch} dataResearchedProducts={dataResearchedProduct} />
                        </div> 
                    )} 
                </>
            )}
        </div>
    )
}

export default MarketResearchDetails
